import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { Collection, LoadingState } from '../../lib/types';
import {
  AsyncRequestProgress,
  addSelectedItemToCollection,
  addSelectedToCollection,
  appSelectors,
  createCollection,
} from '../../redux/appSlice';

interface CollectionSelectProps {
  close: () => void;
  singleResourceId?: number;
}

export const CollectionSelect = ({
  close,
  singleResourceId,
}: CollectionSelectProps) => {
  const navigate = useNavigate();
  const loggedIn: boolean = useAppSelector(appSelectors.isLoggedIn);
  const addCollectionResourceLoadingState: LoadingState = useAppSelector(
    appSelectors.addCollectionResourcesLoadingState
  );
  const createCollectionLoadingState: AsyncRequestProgress = useAppSelector(
    appSelectors.createCollectionLoadingState
  );
  const dispatch = useAppDispatch();
  const userCollections: Collection[] = useAppSelector(
    appSelectors.userCollections
  );
  const [selectedCollectionId, setSelectedCollectionId] = useState<
    number | undefined
  >(undefined);
  const [removeSelected, setRemoveSelected] = useState<boolean>(true);
  const [newCollectionName, setNewCollectionName] = useState<string>('');

  // useEffect(() => {
  //   setSelectedCollectionId(() => {
  //     return userCollections && userCollections.length > 0
  //       ? userCollections[0].ref
  //       : undefined;
  //   });
  // }, [userCollections]);

  useEffect(() => {
    if (
      addCollectionResourceLoadingState === 'succeeded' &&
      selectedCollectionId &&
      !singleResourceId
    ) {
      const selectedCollection = userCollections.find(
        (collection) => collection.ref === selectedCollectionId
      );

      if (selectedCollection) {
        navigate(
          `/collection?id=${selectedCollection.ref}&name=${selectedCollection.name}`
        );
        close();
      }
    } else if (
      addCollectionResourceLoadingState === 'succeeded' &&
      singleResourceId
    ) {
      close();
    }
  }, [
    addCollectionResourceLoadingState,
    close,
    navigate,
    selectedCollectionId,
    singleResourceId,
    userCollections,
  ]);

  useEffect(() => {
    if (
      createCollectionLoadingState.loadingState === 'succeeded' &&
      createCollectionLoadingState.result
    ) {
      setSelectedCollectionId(createCollectionLoadingState.result as number);
      setNewCollectionName('');
    }
  }, [createCollectionLoadingState]);

  const handleCollectionSelectChange = (
    event: ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedCollectionId(parseInt(event.target.value));
  };

  const handleAddResourceToCollectionClick = useCallback(() => {
    if (selectedCollectionId && !singleResourceId) {
      dispatch(
        addSelectedToCollection({ selectedCollectionId, removeSelected })
      );
    }
    if (singleResourceId && selectedCollectionId) {
      dispatch(
        addSelectedItemToCollection({
          selectedCollectionId,
          resourceId: singleResourceId,
        })
      );
    }
  }, [selectedCollectionId, singleResourceId, dispatch, removeSelected]);

  const handleRemoveSelectedChange = (event: ChangeEvent<HTMLInputElement>) => {
    setRemoveSelected(event.target.checked);
  };

  const handleNewCollectionNameChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setNewCollectionName(event.target.value);
  };

  const handleCreateCollectionClick = useCallback(() => {
    if (newCollectionName) {
      dispatch(createCollection(newCollectionName));
    }
  }, [newCollectionName, dispatch]);

  return (
    <div className="collection-select-content">
      {loggedIn && userCollections && userCollections.length > 0 && (
        <>
          <div className="collection-select-form">
            <div className="collection-select-collection-options">
              <div>
                <p>Select a collection from the list below</p>
                <select
                  className="collection-list"
                  value={selectedCollectionId}
                  onChange={handleCollectionSelectChange}
                  disabled={addCollectionResourceLoadingState === 'pending'}
                >
                  <option value={undefined}>Select a collection</option>
                  {userCollections.map(
                    (collection: Collection, index: number) => (
                      <option
                        className="collection-list-item"
                        key={`collection-list-${collection.ref}-${index}`}
                        value={collection.ref}
                      >
                        {collection.name}
                      </option>
                    )
                  )}
                </select>
              </div>
              <div>
                <p>or create a new Collection</p>
                <input
                  value={newCollectionName}
                  placeholder="Collection name"
                  onChange={handleNewCollectionNameChange}
                  disabled={
                    createCollectionLoadingState.loadingState === 'pending'
                  }
                />
                <button
                  onClick={handleCreateCollectionClick}
                  disabled={
                    createCollectionLoadingState.loadingState === 'pending'
                  }
                >
                  Create collection
                </button>
                {createCollectionLoadingState.loadingState === 'failed' && (
                  <p className="error-message">
                    Failed to create your new collection
                  </p>
                )}
              </div>
            </div>
            {!singleResourceId && (
              <div className="input-wrapper-checkbox">
                <input
                  checked={removeSelected}
                  onChange={handleRemoveSelectedChange}
                  id="collection-add-remove-selections"
                  type="checkbox"
                />
                <label htmlFor="collection-add-remove-selections">
                  Remove images from selected bar after adding
                </label>
              </div>
            )}

            <div>
              <button
                className="collection-add-button"
                onClick={handleAddResourceToCollectionClick}
                disabled={
                  !selectedCollectionId ||
                  addCollectionResourceLoadingState === 'pending'
                }
              >
                {singleResourceId ? 'Add image' : 'Add images'}
              </button>
            </div>
          </div>
        </>
      )}
      {!loggedIn && <p>You are not currently logged in.</p>}
    </div>
  );
};
